import { useCallback, useEffect, useMemo, useState } from 'react';
import BracketHeightRangePanel from '../components/bracket/BracketHeightRangePanel';
import BracketResultsPanel from '../components/bracket/BracketResultsPanel';
import BracketSetView from '../components/bracket/BracketSetView';
import TournamentSelector from '../components/bracket/TournamentSelector';
import { getApiErrorMessage } from '../api/client';
import { fetchTournaments } from '../api/competitionApplication';
import usePublicBracket from '../hooks/usePublicBracket';
import { useI18n } from '../i18n';
import { computeBracketStats } from '../utils/bracket';
import { formatEventDate, normalizeCompetitionList } from '../utils/competitionList';
import {
  BRACKET_MODES,
  getBracketFormatLabelKey,
  getBracketSetDivisions,
  getBracketSetSummaryLabel,
  isMatchFormat,
} from '../utils/bracketSet';

/** 공개 토너먼트 화면 (조회 전용). 수정은 /admin/tournament 에서만 가능 */
function TournamentPage() {
  const { t } = useI18n();
  const [tournaments, setTournaments] = useState([]);
  const [tournamentsLoading, setTournamentsLoading] = useState(true);
  /** 대회 목록 오류: { key, error? } (문구는 화면에 그릴 때 현재 언어로 만든다) */
  const [tournamentsError, setTournamentsError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const { status, message, bracketSet, participants, results, liveMatches, reload, selectHeightRange } =
    usePublicBracket(selectedId);

  const loadTournaments = useCallback(async () => {
    setTournamentsLoading(true);
    setTournamentsError(null);
    try {
      const list = normalizeCompetitionList(await fetchTournaments());
      setTournaments(list);
      if (list.length === 0) {
        setTournamentsError({ key: 'tournament.errors.noTournaments' });
      }
    } catch (error) {
      setTournamentsError({ key: 'tournament.errors.loadTournaments', error });
      setTournaments([]);
    } finally {
      setTournamentsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTournaments();
  }, [loadTournaments]);

  const selectedTournament = tournaments.find(
    (item) => selectedId != null && String(item.id) === String(selectedId)
  );
  const tournamentsErrorText = tournamentsError
    ? tournamentsError.error
      ? getApiErrorMessage(tournamentsError.error, t(tournamentsError.key))
      : t(tournamentsError.key)
    : null;
  const stats = useMemo(() => computeBracketStats(participants), [participants]);
  const divisions = useMemo(() => getBracketSetDivisions(bracketSet), [bracketSet]);
  const isFinished = results.length > 0;

  return (
    <div className="container container-bracket">
      <div className="page-intro">
        <p className="page-eyebrow">Tournament Bracket</p>
        <h2 className="page-title">{t('tournament.public.title')}</h2>
        <p className="page-desc">{t('tournament.public.desc')}</p>
      </div>

      <TournamentSelector
        tournaments={tournaments}
        loading={tournamentsLoading}
        error={tournamentsErrorText}
        onRetry={loadTournaments}
        selectedId={selectedId}
        onSelect={setSelectedId}
      />

      {selectedId == null && (
        <p className="panel-empty bracket-empty">{t('tournament.selectPrompt')}</p>
      )}

      {selectedId != null && (
        <section className="bracket-result-section">
          {status === 'loading' && (
            <div className="list-status bracket-loading">
              <p className="bracket-loading-text">{message}</p>
              <div className="skeleton-card" />
            </div>
          )}

          {status === 'empty' && <p className="panel-empty bracket-empty">{message}</p>}

          {status === 'error' && (
            <div className="list-status list-status-error">
              <p>{message}</p>
              <button type="button" className="btn btn-retry" onClick={() => reload()}>
                {t('common.retry')}
              </button>
            </div>
          )}

          {status === 'ready' && bracketSet && (
            <>
              <div className="bracket-header">
                <h2 className="bracket-title">
                  <span className="trophy">🏆</span>
                  {selectedTournament?.name} | {getBracketSetSummaryLabel(bracketSet)}
                </h2>
                <div className="bracket-meta">
                  {selectedTournament?.eventAt && (
                    <div className="bracket-meta-item">
                      📅 {t('tournament.meta.date')}: <strong>{formatEventDate(selectedTournament.eventAt)}</strong>
                    </div>
                  )}
                  {selectedTournament?.location && (
                    <div className="bracket-meta-item">
                      📍 {t('tournament.meta.venue')}: <strong>{selectedTournament.location}</strong>
                    </div>
                  )}
                  <div className="bracket-meta-item">
                    📊 {t('tournament.meta.format')}:{' '}
                    <strong>{t(getBracketFormatLabelKey(bracketSet))}</strong>
                  </div>
                  <div className="bracket-meta-item">
                    🥋 {t('tournament.meta.event')}: <strong>{t('tournament.meta.eventName')}</strong>
                  </div>
                  <div className="bracket-meta-item">
                    🏁 {t('tournament.meta.status')}:{' '}
                    <strong>{t(isFinished ? 'tournament.meta.finished' : 'tournament.meta.inProgress')}</strong>
                  </div>
                </div>
                <div className="stats-grid">
                  <div className="stat-box">
                    <div className="stat-label">{t('tournament.stats.total')}</div>
                    <div className="stat-value">{t('common.people', { count: stats.total })}</div>
                  </div>
                  <div className="stat-box">
                    <div className="stat-label">{t('tournament.stats.maleFemale')}</div>
                    <div className="stat-value">
                      {stats.male} / {stats.female}
                    </div>
                  </div>
                  <div className="stat-box">
                    <div className="stat-label">{t('tournament.stats.avgHeight')}</div>
                    <div className="stat-value">
                      {stats.avgHeight === '-' ? '-' : t('common.cm', { value: stats.avgHeight })}
                    </div>
                  </div>
                  <div className="stat-box">
                    <div className="stat-label">{t('tournament.stats.gyms')}</div>
                    <div className="stat-value">{t('tournament.stats.gymCount', { count: stats.dojos })}</div>
                  </div>
                </div>
              </div>

              <div className="bracket-wrap">
                {bracketSet.mode === BRACKET_MODES.HEIGHT && (
                  <BracketHeightRangePanel
                    bracketSet={bracketSet}
                    participants={participants}
                    editMode={false}
                    onApplyRanges={() => {}}
                    onSelectRange={selectHeightRange}
                  />
                )}
                <BracketSetView bracketSet={bracketSet} divisions={divisions} liveMatches={liveMatches} />
              </div>

              {isFinished && (
                <BracketResultsPanel
                  results={results}
                  bracketSet={bracketSet}
                  matchList={isMatchFormat(bracketSet)}
                />
              )}
            </>
          )}
        </section>
      )}
    </div>
  );
}

export default TournamentPage;
